import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useLanguage } from "../LanguageContext";
import { translations } from "../translations";

export default function MobMenu({ Menus }) {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [clicked, setClicked] = useState(null);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const navigate = useNavigate();

  const languages = ["English", "Burmese", "Chinese"];

  const languageLabels = {
    English: {
      English: "English",
      Burmese: "Burmese",
      Chinese: "Chinese",
    },
    Burmese: {
      English: "အင်္ဂလိပ်",
      Burmese: "ဗမာ",
      Chinese: "တရုတ်",
    },
    Chinese: {
      English: "英语",
      Burmese: "缅甸语",
      Chinese: "中文",
    },
  };

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
    setClicked(null);
    setShowLanguageDropdown(false);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    setClicked(null);
    setShowLanguageDropdown(false);
  };

  const handleMenuClick = (index, hasSubMenu, url) => {
    if (hasSubMenu) {
      setClicked(clicked === index ? null : index);
    } else {
      navigate(url);
      closeDrawer();
    }
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setShowLanguageDropdown(false);
  };

  // Drop-down animation for main menu
  const menuAnimation = {
    hidden: { opacity: 0, y: "-100%" },
    visible: {
      opacity: 1,
      y: "0%",
      transition: { duration: 0.4, ease: "easeInOut" },
    },
    exit: {
      opacity: 0,
      y: "-100%",
      transition: { duration: 0.3, ease: "easeInOut" },
    },
  };

  // Expandable submenu animation
  const subMenuAnimation = {
    enter: {
      opacity: 1,
      height: "auto",
      overflow: "visible",
      transition: { duration: 0.3 },
    },
    exit: {
      opacity: 0,
      height: 0,
      overflow: "hidden",
      transition: { duration: 0.3 },
    },
  };

  return (
    <div>
      <button className="lg:hidden z-[999] relative" onClick={toggleDrawer}>
        {isOpen ? <X /> : <Menu />}
      </button>

      <motion.div
        className="fixed left-0 right-0 top-16 w-full bg-black/90 backdrop-blur text-white p-6 pb-6 shadow-lg rounded-b-lg"
        initial="hidden"
        animate={isOpen ? "visible" : "exit"}
        variants={menuAnimation}
      >
        <ul>
          {Menus.map(({ name, subMenu, url }, i) => {
            const isClicked = clicked === i;
            const hasSubMenu = subMenu?.length;

            return (
              <li key={name}>
                <span
                  className="flex justify-between items-center p-4 hover:bg-white/5 rounded-md cursor-pointer"
                  onClick={() => handleMenuClick(i, hasSubMenu, url)}
                >
                  {name}
                  {hasSubMenu && (
                    <ChevronDown
                      className={`ml-auto transition-transform ${
                        isClicked ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </span>

                {hasSubMenu && (
                  <motion.ul
                    initial="exit"
                    animate={isClicked ? "enter" : "exit"}
                    variants={subMenuAnimation}
                    className="ml-5"
                  >
                    {subMenu.map(
                      ({ name: subName, url: subUrl, icon: Icon }, i) => (
                        <li
                          key={i}
                          className="p-2 flex items-center gap-x-2 cursor-pointer hover:bg-white/5 rounded-md"
                          onClick={closeDrawer}
                        >
                          {Icon && <Icon size={17} />}
                          <NavLink to={subUrl} className="ml-2">
                            {subName}
                          </NavLink>
                        </li>
                      )
                    )}
                  </motion.ul>
                )}
              </li>
            );
          })}

          {/* Language Dropdown in Mobile Menu */}
          <li className="mt-4 ">
            <span
              className="flex justify-between items-center p-4 hover:bg-white/5 rounded-md cursor-pointer"
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
            >
              {translations[language].Language} :{" "}
              {languageLabels[language][language]}{" "}
              <ChevronDown
                className={`ml-auto transition-transform ${
                  showLanguageDropdown ? "rotate-180" : ""
                }`}
              />
            </span>

            {showLanguageDropdown && (
              <motion.ul
                initial="exit"
                animate={showLanguageDropdown ? "enter" : "exit"}
                variants={subMenuAnimation}
                className="ml-5"
              >
                {languages.map((lang) => (
                  <li
                    key={lang}
                    className="p-2 cursor-pointer hover:bg-white/5 rounded-md"
                    onClick={() => handleLanguageChange(lang)}
                  >
                    {languageLabels[language][lang]}
                  </li>
                ))}
              </motion.ul>
            )}
          </li>
        </ul>
      </motion.div>
    </div>
  );
}
